/**
 * Finance dashboard — revenue, refunds, MRR and buyer billing, live from Stripe.
 *
 * Mounted in BOTH consoles (see UtilizationDashboard for the same note): never
 * import `api` and use it directly here, or a super admin request would carry
 * the portal token and bounce them to the portal sign-in page.
 *
 * Every figure is read from the Stripe API when the page loads — there is no
 * local ledger. Three things follow from that and all three are shown rather
 * than hidden, because a finance number whose age and completeness are
 * invisible is a number nobody can safely act on:
 *
 *   - "Updated Xm ago" plus a Refresh button that bypasses the server's short
 *     cache;
 *   - a truncation warning when the range hit the pagination cap, so a partial
 *     total is never read as the whole;
 *   - the unattributed total, i.e. money Stripe has that cannot be matched to a
 *     portal account.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeDollarSign,
  Ban,
  CreditCard,
  ExternalLink,
  Landmark,
  Receipt,
  RefreshCw,
  Search,
  TrendingUp,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { EmptyPanel, PageHero, PageShell } from "@/components/interior-page";
import {
  BarChart,
  CAT,
  ChartFrame,
  FilterBar,
  RangePicker,
  StatTile,
  money,
  useAgo,
} from "@/components/charts";
import { api, type ApiClient } from "@/lib/api";
import { cn } from "@/lib/utils";

type Granularity = "day" | "month" | "year";

interface FinancePoint {
  period: string;
  gross: number;
  refunds: number;
  net: number;
  fees: number;
  succeeded_count: number;
  failed_count: number;
}

interface CustomerRow {
  stripe_customer_id: string;
  user_id?: string | null;
  email?: string | null;
  name?: string | null;
  org_name?: string | null;
  tier_name?: string | null;
  sub_status?: string | null;
  gross: number;
  refunds: number;
  net: number;
  fees: number;
  charges: number;
  failed: number;
  last_payment: string | null;
  linked: boolean;
}

interface ChargeRow {
  id: string;
  created: string;
  amount: number;
  refunded: number;
  status: string;
  currency?: string;
  description?: string | null;
  receipt_url?: string | null;
  failure_message?: string | null;
  email?: string | null;
  name?: string | null;
  org_name?: string | null;
  user_id?: string | null;
}

interface Finance {
  configured: boolean;
  reason: string | null;
  generated_at: string;
  cached: boolean;
  truncated: boolean;
  granularity?: Granularity;
  range?: { start: string; end: string };
  totals: {
    gross: number; refunds: number; net: number; fees: number; payout: number;
    succeeded_count: number; failed_count: number; refund_count: number;
    paying_customers: number; avg_transaction: number; failure_rate: number;
  };
  previous: { gross: number; net: number };
  previous_available: boolean;
  previous_range?: { start: string; end: string };
  recurring: {
    mrr: number; arr: number; trialing_mrr: number; at_risk_mrr: number;
    active_subscriptions: number; trialing_subscriptions: number;
    past_due_subscriptions: number; scheduled_cancellations: number;
  };
  series: FinancePoint[];
  customers: CustomerRow[];
  recent: ChargeRow[];
  failures: ChargeRow[];
  unattributed: { gross: number; count: number };
}

interface BuyerRow {
  user_id: string;
  email: string;
  name: string;
  account_status: string;
  org_name: string | null;
  created_at: string | null;
  last_activity_at: string | null;
  tier_id: number | null;
  tier_name: string | null;
  tier_amount: number | null;
  sub_status: string | null;
  sub_active: boolean | null;
  started_at: string | null;
  next_billing_date: string | null;
  cancel_at_period_end: boolean | null;
  has_stripe: boolean;
  has_subscription_id: boolean;
}

interface CustomerDetail {
  account: {
    user_id: string; email: string; name: string; org_name: string | null;
    created_at: string | null; tier_name: string | null; sub_status: string | null;
    sub_active: boolean | null; next_billing_date: string | null;
    cancel_at_period_end: boolean | null;
    stripe_customer_id: string | null; stripe_subscription_id: string | null;
  };
  stripe: {
    configured: boolean;
    found?: boolean;
    reason?: string;
    customer?: { id: string; email: string | null; name: string | null; created: string | null; delinquent: boolean; balance: number; currency: string };
    card?: { brand: string; last4: string; exp_month: number; exp_year: number } | null;
    lifetime?: { gross: number; refunded: number; net: number; charge_count: number };
    subscriptions?: { id: string; status: string; cancel_at_period_end: boolean; created: string | null; current_period_end: string | null; canceled_at: string | null; monthly_value: number; plan: string }[];
    invoices?: { id: string; number: string | null; status: string | null; created: string | null; amount_due: number; amount_paid: number; hosted_invoice_url: string | null; invoice_pdf: string | null }[];
    charges?: { id: string; created: string | null; amount: number; refunded: number; status: string; description: string | null; failure_message: string | null; receipt_url: string | null }[];
  };
}

const GRANULARITIES: { value: Granularity; label: string }[] = [
  { value: "day", label: "Daily" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Annual" },
];

const RANGE_LABEL: Record<Granularity, string> = {
  day: "Last 30 days, by day",
  month: "Last 12 months, by month",
  year: "Last 3 years, by year",
};

const formatPeriod = (p: string, g: Granularity): string => {
  if (g === "year") return p;
  if (g === "month") {
    const [y, m] = p.split("-");
    return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleDateString(undefined, {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    });
  }
  return new Date(`${p}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const formatPeriodLong = (p: string, g: Granularity): string => {
  if (g === "year") return p;
  if (g === "month") {
    const [y, m] = p.split("-");
    return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  return new Date(`${p}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const shortDateTime = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";

/**
 * Org names are long enough to dominate a narrow column, so the two tenants are
 * shown by the initials everyone uses for them. Anything else prints in full.
 */
const ORG_SHORT: Record<string, string> = {
  businesslocating: "BL",
  "ecashflow acquisitions": "ECFA",
};

const orgShort = (name: string | null | undefined): string =>
  name ? ORG_SHORT[name.trim().toLowerCase()] ?? name : "—";

/** Status pill. Semantic colours stay translucent so they sit in the dark theme. */
function StatusPill({ status }: { status: string | null | undefined }) {
  const s = (status || "").toLowerCase();
  const tone =
    s === "active" || s === "paid" || s === "succeeded"
      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
      : s === "past_due" || s === "open" || s === "unpaid"
        ? "bg-amber-500/15 text-amber-400 border-amber-500/25"
        : s === "canceled" || s === "cancelled" || s === "failed"
          ? "bg-rose-500/15 text-rose-400 border-rose-500/25"
          : "bg-muted text-muted-foreground border-border/70";
  return (
    <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-sm font-semibold capitalize", tone)}>
      {status ? status.replace(/_/g, " ") : "none"}
    </span>
  );
}

export default function FinanceDashboardPage({
  client = api,
}: { client?: ApiClient } = {}) {
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [data, setData] = useState<Finance | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [buyers, setBuyers] = useState<{ total: number; data: BuyerRow[] } | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const PAGE_SIZE = 25;

  const load = useCallback(
    async (opts?: { refresh?: boolean }) => {
      if (opts?.refresh) setRefreshing(true);
      else setLoading(true);
      try {
        const d = await client.get<Finance>(
          `/analytics/finance?granularity=${granularity}${opts?.refresh ? "&refresh=true" : ""}`,
        );
        setData(d);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load finance data");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [client, granularity],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Buyer roster is a separate, database-only call — deliberately not part of
  // the Stripe pull, so paging and searching it never costs a Stripe round trip.
  useEffect(() => {
    const params = new URLSearchParams({
      skip: String(page * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (search.trim()) params.set("search", search.trim());
    const t = setTimeout(() => {
      client
        .get<{ total: number; data: BuyerRow[] }>(`/analytics/buyers?${params}`)
        .then(setBuyers)
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [client, page, search]);

  const openDetail = async (userId: string) => {
    setDetailLoading(true);
    try {
      const d = await client.get<CustomerDetail>(`/analytics/finance/customer/${userId}`);
      setDetail(d);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const ago = useAgo(data?.generated_at);

  // Net and refunds are the two parts of gross, so they stack. Gold carries the
  // money that was kept; the second categorical slot carries what went back.
  const revenueSeries = useMemo(
    () => [
      { key: "net", label: "Net revenue", color: CAT[0] },
      { key: "refunds", label: "Refunded", color: CAT[1] },
    ],
    [],
  );

  if (loading) return <Loader className="min-h-[60vh]" />;

  if (error || !data) {
    return (
      <PageShell>
        <EmptyPanel
          icon={AlertTriangle}
          title="Finance data unavailable"
          description={error || "The finance summary could not be loaded right now."}
        />
      </PageShell>
    );
  }

  const t = data.totals;
  const r = data.recurring;
  const hasRefunds = data.series.some((s) => s.refunds > 0);
  const chartSeries = hasRefunds ? revenueSeries : [revenueSeries[0]];

  return (
    <PageShell>
      <PageHero
        eyebrow="Finance"
        icon={BadgeDollarSign}
        title="Every dollar the platform has taken,"
        titleAccent="in one live view."
        description="Revenue, refunds, processing fees, failed charges and recurring value — read live from the payment processor each time this page loads. Switch between daily, monthly and annual views below."
        aside={
          <>
            <div className="text-base font-bold uppercase tracking-[0.18em] text-primary/80">
              {RANGE_LABEL[granularity]}
            </div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">
              {money(t.net)}
            </div>
            <p className="mt-2 text-base leading-7 text-muted-foreground">
              net across {t.succeeded_count.toLocaleString()} successful payments from{" "}
              {t.paying_customers.toLocaleString()} customers. {money(r.mrr)} MRR /{" "}
              {money(r.arr)} ARR on {r.active_subscriptions} active subscriptions.
            </p>
          </>
        }
      />

      {/* Controls live here, NOT on the hero banner — see FilterBar. */}
      <FilterBar
        meta={
          <>
            {ago && (
              <span>
                Updated {ago}
                {data.cached && " · cached"}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => void load({ refresh: true })}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              Refresh
            </Button>
          </>
        }
      >
        <RangePicker
          options={GRANULARITIES}
          value={granularity}
          onChange={setGranularity}
          label="View"
        />
      </FilterBar>

      {!data.configured && (
        <p className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5 text-base leading-6 text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Payments are not configured in this environment{data.reason ? ` — ${data.reason}` : ""}.
            Every figure below reads zero until a secret key is set.
          </span>
        </p>
      )}

      {data.truncated && (
        <p className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5 text-base leading-6 text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This range hit the pagination limit, so the totals below are a floor rather than
            the complete picture. Narrow the range for exact figures.
          </span>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Net revenue"
          value={money(t.net, { compact: true })}
          tone="gold"
          delta={data.previous_available ? { current: t.net, previous: data.previous.net } : null}
          deltaLabel="vs previous period"
          hint={data.previous_available ? undefined : "No comparison on ranges this long"}
        />
        <StatTile
          label="MRR"
          value={money(r.mrr, { compact: true })}
          hint={`${r.active_subscriptions} active subscriptions`}
        />
        <StatTile label="ARR" value={money(r.arr, { compact: true })} hint="MRR × 12" />
        <StatTile
          label="Paid out"
          value={money(t.payout, { compact: true })}
          hint={`After ${money(t.fees)} in processing fees`}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Gross charged"
          value={money(t.gross, { compact: true })}
          hint={`${t.succeeded_count.toLocaleString()} successful payments`}
        />
        <StatTile
          label="Refunded"
          value={money(t.refunds, { compact: true })}
          invertDelta
          hint={`${t.refund_count} charge${t.refund_count === 1 ? "" : "s"} refunded`}
        />
        <StatTile
          label="Failed payments"
          value={t.failed_count.toLocaleString()}
          invertDelta
          hint={`${t.failure_rate}% of attempts declined`}
        />
        <StatTile
          label="Average payment"
          value={money(t.avg_transaction)}
          hint={`${t.paying_customers.toLocaleString()} paying customers`}
        />
      </div>

      <ChartFrame
        title={`Revenue by ${granularity === "day" ? "day" : granularity === "month" ? "month" : "year"}`}
        subtitle={
          hasRefunds
            ? "Net kept and refunded stack to the gross charged. Refunds sit on the date of the original charge, so an older period can move when a refund is issued."
            : "Net revenue per period. No refunds were issued in this range."
        }
        series={chartSeries}
        rows={data.series as unknown as Record<string, string | number>[]}
        columns={[
          { key: "period", label: "Period", format: (v) => formatPeriodLong(String(v), granularity) },
          { key: "gross", label: "Gross", format: (v) => money(Number(v)) },
          { key: "refunds", label: "Refunded", format: (v) => money(Number(v)) },
          { key: "net", label: "Net", format: (v) => money(Number(v)) },
          { key: "fees", label: "Fees", format: (v) => money(Number(v)) },
          { key: "succeeded_count", label: "Payments" },
          { key: "failed_count", label: "Failed" },
        ]}
      >
        <BarChart
          data={data.series as unknown as Record<string, string | number>[]}
          xKey="period"
          series={chartSeries}
          height={280}
          formatValue={(n) => money(n, { compact: true })}
          formatTooltipValue={(n) => money(n)}
          formatX={(p) => formatPeriod(p, granularity)}
          formatTooltipX={(p) => formatPeriodLong(p, granularity)}
        />
      </ChartFrame>

      <div className="grid gap-5 xl:grid-cols-3">
        <ChartFrame
          title="Subscription health"
          subtitle="Live subscription states right now, not for the selected range."
          className="xl:col-span-1"
        >
          <div className="space-y-3">
            {[
              { label: "Active", value: r.active_subscriptions, tone: "emerald" },
              { label: "Trialing", value: r.trialing_subscriptions, tone: "amber" },
              { label: "Past due", value: r.past_due_subscriptions, tone: "rose" },
              { label: "Cancelling at period end", value: r.scheduled_cancellations, tone: "amber" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3">
                <span className="text-base text-muted-foreground">{row.label}</span>
                <span className="text-base font-bold tabular-nums text-foreground">
                  {row.value.toLocaleString()}
                </span>
              </div>
            ))}
            <div className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2">
              <div className="text-sm font-bold uppercase tracking-[0.14em] text-amber-400">
                Revenue at risk
              </div>
              <div className="mt-1 text-lg font-extrabold tabular-nums text-foreground">
                {money(r.at_risk_mrr)}
                <span className="ml-1.5 text-sm font-normal text-muted-foreground">/ month</span>
              </div>
              <p className="mt-1.5 text-base leading-5 text-muted-foreground">
                Past-due subscriptions plus those already scheduled to cancel.
              </p>
            </div>
          </div>
        </ChartFrame>

        <ChartFrame
          title="Top customers by net revenue"
          subtitle="Within the selected range. Click a linked customer to open their full billing record."
          className="xl:col-span-2"
          rows={data.customers as unknown as Record<string, string | number>[]}
          columns={[
            { key: "email", label: "Customer", format: (v, row) => String(row.name || v || row.stripe_customer_id) },
            { key: "net", label: "Net", format: (v) => money(Number(v)) },
            { key: "charges", label: "Payments" },
          ]}
        >
          {data.customers.length === 0 ? (
            <p className="text-base text-muted-foreground">No payments in this range.</p>
          ) : (
            <div className="max-h-[19rem] overflow-auto">
              <table className="w-full min-w-[36rem] text-left text-base">
                <thead className="sticky top-0 bg-card text-muted-foreground">
                  <tr className="border-b border-border/70">
                    <th className="px-2 py-2.5 font-semibold">Customer</th>
                    <th className="px-2 py-2.5 font-semibold">Plan</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Net</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Payments</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Last paid</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.slice(0, 25).map((c) => (
                    <tr key={c.stripe_customer_id} className="border-b border-border/50 last:border-0">
                      <td className="px-2 py-2">
                        {c.linked && c.user_id ? (
                          <button
                            type="button"
                            onClick={() => void openDetail(c.user_id!)}
                            className="text-left font-semibold text-primary hover:underline"
                          >
                            {c.name || c.email}
                          </button>
                        ) : (
                          <span className="font-semibold text-foreground">
                            {c.name || c.email || c.stripe_customer_id}
                          </span>
                        )}
                        <div className="text-muted-foreground">
                          {c.linked ? c.email : "Not linked to a portal account"}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">{c.tier_name ?? "—"}</td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums text-foreground">
                        {money(c.net)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {c.charges}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {shortDateTime(c.last_payment)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartFrame>
      </div>

      {data.unattributed.count > 0 && (
        <p className="flex items-start gap-2.5 rounded-xl border border-border/70 bg-muted/30 px-4 py-3.5 text-base leading-6 text-muted-foreground">
          <Landmark className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {money(data.unattributed.gross)} across {data.unattributed.count} payment
            {data.unattributed.count === 1 ? "" : "s"} in this range came from billing accounts
            with no matching portal account, so they are counted in the totals but cannot be
            attributed to a buyer or organization. This usually means the account was deleted,
            or the payment was taken outside the app.
          </span>
        </p>
      )}

      {data.failures.length > 0 && (
        <ChartFrame
          title="Failed payments"
          subtitle="Declined charges in this range — the recoverable ones are worth a call."
        >
          <div className="max-h-[18rem] overflow-auto">
            <table className="w-full min-w-[42rem] text-left text-base">
              <thead className="sticky top-0 bg-card text-muted-foreground">
                <tr className="border-b border-border/70">
                  <th className="px-2 py-2.5 font-semibold">When</th>
                  <th className="px-2 py-2.5 font-semibold">Customer</th>
                  <th className="px-2 py-2.5 text-right font-semibold">Amount</th>
                  <th className="px-2 py-2.5 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.failures.map((f) => (
                  <tr key={f.id} className="border-b border-border/50 last:border-0">
                    <td className="px-2 py-2 tabular-nums text-muted-foreground">
                      {shortDateTime(f.created)}
                    </td>
                    <td className="px-2 py-2 text-foreground">{f.name || f.email || "—"}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-foreground">
                      {money(f.amount)}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {f.failure_message || "Declined"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartFrame>
      )}

      <ChartFrame
        title="Recent payments"
        subtitle="Newest first, with a link to the receipt for each."
      >
        {data.recent.length === 0 ? (
          <p className="text-base text-muted-foreground">No payments in this range.</p>
        ) : (
          <div className="max-h-[22rem] overflow-auto">
            <table className="w-full min-w-[44rem] text-left text-base">
              <thead className="sticky top-0 bg-card text-muted-foreground">
                <tr className="border-b border-border/70">
                  <th className="px-2 py-2.5 font-semibold">When</th>
                  <th className="px-2 py-2.5 font-semibold">Customer</th>
                  <th className="px-2 py-2.5 font-semibold">Description</th>
                  <th className="px-2 py-2.5 text-right font-semibold">Amount</th>
                  <th className="px-2 py-2.5 text-right font-semibold">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((c) => (
                  <tr key={c.id} className="border-b border-border/50 last:border-0">
                    <td className="px-2 py-2 tabular-nums text-muted-foreground">
                      {shortDateTime(c.created)}
                    </td>
                    <td className="px-2 py-2">
                      {c.user_id ? (
                        <button
                          type="button"
                          onClick={() => void openDetail(c.user_id!)}
                          className="text-left font-semibold text-primary hover:underline"
                        >
                          {c.name || c.email}
                        </button>
                      ) : (
                        <span className="text-foreground">{c.name || c.email || "—"}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">{c.description || "—"}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-foreground">
                      {money(c.amount)}
                      {c.refunded > 0 && (
                        <span className="ml-1.5 text-sm font-normal text-rose-400">
                          −{money(c.refunded)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {c.receipt_url ? (
                        <a
                          href={c.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartFrame>

      {/* ── Buyer roster with billing state ────────────────────────── */}
      <ChartFrame
        title="Buyers & billing"
        subtitle="Every buyer with the plan and renewal date held on the platform. Open one to pull their live billing record."
        actions={
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search name or email"
              className="h-9 w-60 rounded-lg border border-border/80 bg-card pl-9 pr-3 text-base text-foreground outline-none focus:border-primary/50"
            />
          </div>
        }
      >
        {!buyers ? (
          <p className="text-base text-muted-foreground">Loading buyers…</p>
        ) : buyers.data.length === 0 ? (
          <p className="text-base text-muted-foreground">No buyers match that search.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[52rem] text-left text-base">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-border/70">
                    <th className="px-2 py-2.5 font-semibold">Buyer</th>
                    <th className="px-2 py-2.5 font-semibold">Org</th>
                    <th className="px-2 py-2.5 font-semibold">Plan</th>
                    <th className="px-2 py-2.5 font-semibold">Billing</th>
                    <th className="px-2 py-2.5 font-semibold">Renews</th>
                    <th className="px-2 py-2.5 text-right font-semibold">Card</th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.data.map((b) => (
                    <tr key={b.user_id} className="border-b border-border/50 last:border-0">
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => void openDetail(b.user_id)}
                          className="text-left font-semibold text-primary hover:underline"
                        >
                          {b.name}
                        </button>
                        <div className="text-muted-foreground">{b.email}</div>
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">{orgShort(b.org_name)}</td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {b.tier_name ?? "—"}
                        {b.tier_amount != null && b.tier_name && (
                          <span className="ml-1 text-sm">
                            (${b.tier_amount}/mo)
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <StatusPill status={b.sub_status} />
                        {b.cancel_at_period_end && (
                          <div className="mt-1 text-sm text-amber-400">Cancelling</div>
                        )}
                      </td>
                      <td className="px-2 py-2 tabular-nums text-muted-foreground">
                        {shortDateTime(b.next_billing_date)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {b.has_stripe ? (
                          <CreditCard className="ml-auto h-4 w-4 text-emerald-400" />
                        ) : (
                          <Ban className="ml-auto h-4 w-4 text-muted-foreground/60" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between pt-4 text-base text-muted-foreground">
              <span>
                {buyers.total.toLocaleString()} buyer{buyers.total === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="tabular-nums">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, buyers.total)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(page + 1) * PAGE_SIZE >= buyers.total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </ChartFrame>

      <p className="flex items-start gap-2.5 text-base leading-6 text-muted-foreground">
        <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Figures are pulled from the payment processor each load and briefly cached server-side; Refresh
          forces a fresh read. Refunds are attributed to the date of the charge they reverse,
          so historical periods can change when a refund is issued.
        </span>
      </p>

      {(detail || detailLoading) && (
        <CustomerDetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}
    </PageShell>
  );
}

/** The "don't open Stripe" panel: one buyer's complete billing record. */
function CustomerDetailModal({
  detail,
  loading,
  onClose,
}: {
  detail: CustomerDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const s = detail?.stripe;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-foreground/50 backdrop-blur-[2px]"
      />
      <div className="relative z-10 w-full max-w-3xl rounded-2xl border border-border/80 bg-card p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg border border-border/80 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        {loading || !detail ? (
          <Loader className="min-h-[16rem]" />
        ) : (
          <>
            <div className="pr-10">
              <h2 className="heading-bold text-2xl tracking-tight text-foreground">
                {detail.account.name}
              </h2>
              <p className="text-base text-muted-foreground">
                {detail.account.email}
                {detail.account.org_name && <> · {orgShort(detail.account.org_name)}</>}
              </p>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
                <div className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Plan
                </div>
                <div className="mt-1.5 text-lg font-bold text-foreground">
                  {detail.account.tier_name ?? "None"}
                </div>
                <div className="mt-1">
                  <StatusPill status={detail.account.sub_status} />
                </div>
              </div>
              <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
                <div className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Card on file
                </div>
                <div className="mt-1.5 text-lg font-bold capitalize text-foreground">
                  {s?.card ? `${s.card.brand} ···· ${s.card.last4}` : "None"}
                </div>
                {s?.card && (
                  <div className="mt-1 text-base text-muted-foreground">
                    Expires {String(s.card.exp_month).padStart(2, "0")}/{s.card.exp_year}
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
                <div className="text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Recent net
                </div>
                <div className="mt-1.5 text-lg font-bold text-foreground">
                  {s?.lifetime ? money(s.lifetime.net) : "—"}
                </div>
                <div className="mt-1 text-base text-muted-foreground">
                  {s?.lifetime ? `${s.lifetime.charge_count} recent charges` : "No billing data"}
                </div>
              </div>
            </div>

            {s && s.found === false && (
              <p className="mt-4 rounded-xl border border-border/70 bg-muted/30 px-4 py-3.5 text-base leading-6 text-muted-foreground">
                {s.reason || "No billing record for this account."} Promo-granted and free
                accounts never create a billing customer, so this is expected for them.
              </p>
            )}

            {s?.subscriptions && s.subscriptions.length > 0 && (
              <div className="mt-5">
                <h3 className="heading-bold pb-2.5 text-base uppercase tracking-[0.14em] text-muted-foreground">
                  Subscriptions
                </h3>
                <div className="space-y-2">
                  {s.subscriptions.map((sub) => (
                    <div
                      key={sub.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 px-3.5 py-2.5 text-base"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <StatusPill status={sub.status} />
                          <span className="font-semibold text-foreground">
                            {money(sub.monthly_value)}/mo
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-muted-foreground">{sub.plan}</div>
                      </div>
                      <div className="text-right text-muted-foreground">
                        {sub.cancel_at_period_end && (
                          <div className="font-semibold text-amber-400">Cancels at period end</div>
                        )}
                        <div>Renews {shortDateTime(sub.current_period_end)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {s?.invoices && s.invoices.length > 0 && (
              <div className="mt-5">
                <h3 className="heading-bold pb-2.5 text-base uppercase tracking-[0.14em] text-muted-foreground">
                  Invoices
                </h3>
                <div className="max-h-52 overflow-auto rounded-lg border border-border/70">
                  <table className="w-full text-left text-base">
                    <thead className="sticky top-0 bg-muted/60 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Number</th>
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Paid</th>
                        <th className="px-3 py-2 text-right font-semibold">Links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-border/60">
                          <td className="px-3 py-1.5 text-foreground">{inv.number ?? inv.id}</td>
                          <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                            {shortDateTime(inv.created)}
                          </td>
                          <td className="px-3 py-1.5">
                            <StatusPill status={inv.status} />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                            {money(inv.amount_paid)}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {inv.invoice_pdf ? (
                              <a
                                href={inv.invoice_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline"
                              >
                                PDF <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {s?.charges && s.charges.length > 0 && (
              <div className="mt-5">
                <h3 className="heading-bold pb-2.5 text-base uppercase tracking-[0.14em] text-muted-foreground">
                  Charges
                </h3>
                <div className="max-h-52 overflow-auto rounded-lg border border-border/70">
                  <table className="w-full text-left text-base">
                    <thead className="sticky top-0 bg-muted/60 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Amount</th>
                        <th className="px-3 py-2 text-right font-semibold">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.charges.map((c) => (
                        <tr key={c.id} className="border-t border-border/60">
                          <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                            {shortDateTime(c.created)}
                          </td>
                          <td className="px-3 py-1.5">
                            <StatusPill status={c.status} />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                            {money(c.amount)}
                            {c.refunded > 0 && (
                              <span className="ml-1.5 text-sm text-rose-400">
                                −{money(c.refunded)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {c.receipt_url ? (
                              <a
                                href={c.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline"
                              >
                                <Receipt className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
